import {
  documentKeys,
  type GetDocumentListArgs,
  getDocumentListOptions,
  type GetDocumentListSchema,
} from "@prismicio/editor-fields"
import { uniqBy } from "@prismicio/editor-support/Array"
import { useToast } from "@prismicio/editor-ui"
import {
  type InfiniteData,
  useQuery,
  useQueryClient,
  useSuspenseInfiniteQuery,
} from "@tanstack/react-query"
import { useEffect } from "react"

import { env } from "../../app/env"
import { baseUrl as coreApiBaseUrl } from "../../app/services/core"
import { useTeamSpace } from "../../app/services/role"
import type { Tab } from "../DocumentListHeader/DocumentListHeader"
import {
  useDocumentListSearchParams,
  useRepositoryLanguages,
} from "../useDocumentListSearchParams"

interface UseDocumentListDataArgs {
  tab: Tab
  releaseId?: string
  enableRevalidationToast?: boolean
}

export function useDocumentListData(args: UseDocumentListDataArgs) {
  const { tab, releaseId, enableRevalidationToast = false } = args

  const { searchParams } = useDocumentListSearchParams()
  const { masterLocaleId } = useRepositoryLanguages()
  const customTypes = useSelectedCustomTypes(tab)

  const queryClient = useQueryClient()

  const documentListArgs: GetDocumentListArgs = {
    baseUrl: coreApiBaseUrl,
    authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
    limit: 30,
    statuses:
      searchParams.status ||
      getStatusBasedOnTab({
        tab,
        releaseId,
      }),
    authors: searchParams.authors,
    language: searchParams.language?.[0] || masterLocaleId,
    groupLangIds: searchParams.group_lang_id,
    documentIds: searchParams.document_id,
    customTypes: customTypes,
    tags: searchParams.tags,
    term: searchParams.term?.[0],
  }

  const result = useSuspenseInfiniteQuery({
    queryKey: documentKeys.infiniteList(documentListArgs),
    queryFn: ({ pageParam: cursor, queryKey: [, , params] }) =>
      /**
       * Enables us to share data between regular and infinite queries.
       * The shape of data in cache is different for these. https://github.com/TanStack/query/discussions/1255
       */
      queryClient.fetchQuery(
        getDocumentListOptions({
          ...params,
          // Leave out cursor property to make sure query keys are exactly the same whether we provide cursor: undefined or don't provide at all https://github.com/TanStack/query/issues/3741
          ...(cursor ? { cursor } : {}),
        }),
      ),
    // Shows the same stable data unless explicitly invalidated
    staleTime: Infinity,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    initialPageParam: undefined as Cursor,
    getNextPageParam: (lastPage, allPages) => {
      /**
       * API returns new cursor even for the last page.
       * To determine if there should be a next cursor, we count all documents we have and compare the number to total returned in the last page.
       */
      const numberOfDocuments = allPages.reduce(
        (acc, page) => acc + page.results.length,
        0,
      )
      return numberOfDocuments === lastPage.total ? undefined : lastPage.cursor
    },
    select: selectInfiniteListData,
  })

  // Fetch first page in background to check whether there is new data available
  const refetchInterval = 60_000

  const backgroundResult = useQuery({
    ...getDocumentListOptions(documentListArgs),
    staleTime: refetchInterval,
    refetchInterval: refetchInterval,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    enabled: enableRevalidationToast,
  })

  const showRevalidationToast = Boolean(
    // If revalidation toast is enabled
    enableRevalidationToast &&
      // If queries are not fetching
      !result.isFetching &&
      !backgroundResult.isFetching &&
      // If we have background data to compare against
      backgroundResult.data &&
      // If background data differs from shown
      result.data.documents[0]?.id !== backgroundResult.data.results[0]?.id,
  )

  const { showToast } = useToast()

  useEffect(() => {
    if (!showRevalidationToast) return
    showToast({
      title: "There has been changes to documents",
      action: {
        title: "Update",
        onClick: result.refetch,
      },
      icon: "alert",
      seconds: 10,
    })
  }, [showRevalidationToast, showToast, result.refetch])

  return result
}

function useSelectedCustomTypes(tab: Tab) {
  const shouldFilterByTeamSpace = useShouldFilterByTeamSpace(tab)

  const { searchParams } = useDocumentListSearchParams()
  let searchCustomTypes = searchParams.customTypes || []

  const teamSpace = useTeamSpace(searchParams.space)
  const teamSpaceCustomTypes = teamSpace?.customTypes || []

  if (!shouldFilterByTeamSpace) return searchCustomTypes

  searchCustomTypes = searchCustomTypes.filter((searchCustomType) =>
    teamSpaceCustomTypes.includes(searchCustomType),
  )

  if (searchCustomTypes.length) return searchCustomTypes

  return teamSpaceCustomTypes
}

/**
 * Whether to filter documents by a Team Space, which means the doc list is filtered
 * internally by Team Space's custom types (without setting customTypes filter in the UI)
 */
function useShouldFilterByTeamSpace(tab: Tab) {
  return tab !== "upcoming"
}

/**
 * Checks whether currently selected Team Space is empty (has no custom types)
 */
export function useIsEmptyTeamSpace(tab: Tab) {
  const shouldFilterByTeamSpace = useShouldFilterByTeamSpace(tab)

  const { searchParams } = useDocumentListSearchParams()

  const teamSpace = useTeamSpace(searchParams.space)

  if (!teamSpace) return true

  return shouldFilterByTeamSpace && !teamSpace.customTypes?.length
}

interface GetStatusBasedOnTabArgs {
  tab: Tab
  releaseId?: string
}

function getStatusBasedOnTab(args: GetStatusBasedOnTabArgs) {
  const { tab, releaseId } = args

  if (tab === "upcoming" && !releaseId) {
    return ["release"]
  }

  if (tab === "upcoming" && releaseId) {
    return [`release:${releaseId}`]
  }

  if (tab === "working") {
    return ["unclassified", "published"]
  }

  if (tab === "archived") {
    return ["archived"]
  }

  return []
}

type Cursor = string | undefined

function selectInfiniteListData(
  data: InfiniteData<GetDocumentListSchema, Cursor>,
) {
  return {
    /**
     * Different pages of the same search request can naturally contain duplicates.
     * Remove those duplicates to make the UI looks less weird and React happy.
     */
    documents: uniqBy(
      data.pages.flatMap((page) => page.results),
      (document) => document.id,
    ),
  }
}
